import { ChevronDown, Plus, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAppWorkspace } from "@/contexts/AppWorkspaceContext";
import { useSidebar } from "@/components/ui/sidebar";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

/**
 * AppWorkspaceSelector
 *
 * Backend workspace selector for the sidebar.
 * Allows users to switch between collaborative team workspaces (AppWorkspaces)
 * and create new workspaces. This is distinct from the Environment selector
 * in the header (Production/Staging).
 */
export function AppWorkspaceSelector() {
  const { state } = useSidebar();
  const {
    activeWorkspace,
    workspaces,
    isLoadingWorkspaces,
    switchWorkspace,
    createWorkspace,
  } = useAppWorkspace();

  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [isCreating, setIsCreating] = useState(false);

  const handleCreateWorkspace = async () => {
    if (!newWorkspaceName.trim()) {
      toast.error("Please enter a workspace name");
      return;
    }

    setIsCreating(true);
    try {
      await createWorkspace(newWorkspaceName.trim());
      toast.success("Workspace created successfully");
      setCreateDialogOpen(false);
      setNewWorkspaceName("");
    } catch (error) {
      console.error("Failed to create workspace:", error);
      toast.error("Failed to create workspace");
    } finally {
      setIsCreating(false);
    }
  };

  // Render invisible spacer to maintain layout when sidebar is collapsed
  if (state === "collapsed") {
    return (
      <div className="flex-1">
        <Button
          variant="ghost"
          className="w-full justify-between px-2 py-1.5 h-auto opacity-0 pointer-events-none"
        >
          <span className="text-sm font-semibold">Workspace</span>
          <ChevronDown className="h-4 w-4 ml-2 shrink-0" />
        </Button>
      </div>
    );
  }

  const isLoading = isLoadingWorkspaces || !activeWorkspace;

  return (
    <>
      <div className="flex-1 min-w-0">
        {isLoading ? (
          // Loading skeleton - maintains layout space
          <Button
            variant="ghost"
            className="w-full justify-between px-2 py-1.5 h-auto"
            disabled
          >
            <div className="h-5 bg-muted rounded animate-pulse flex-1 max-w-[100px]" />
            <ChevronDown className="h-4 w-4 opacity-30 ml-2 shrink-0" />
          </Button>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="w-full justify-between px-2 py-1.5 h-auto hover:bg-sidebar-accent"
              >
                <span className="text-sm font-semibold truncate flex-1 text-left">
                  {activeWorkspace.name}
                </span>
                <ChevronDown className="h-4 w-4 opacity-50 ml-2 shrink-0" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[240px]">
              <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
                Your Workspaces
              </div>
              {workspaces.map((workspace) => (
                <DropdownMenuItem
                  key={workspace._id}
                  onClick={() => switchWorkspace(workspace._id)}
                  className="cursor-pointer"
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium truncate">
                      {workspace.name}
                    </span>
                    {workspace._id === activeWorkspace._id && (
                      <Check className="h-4 w-4 ml-2 shrink-0" />
                    )}
                  </div>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setCreateDialogOpen(true)}
                className="cursor-pointer"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create Workspace
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Create Workspace Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Workspace</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="workspace-name">Workspace Name</Label>
              <Input
                id="workspace-name"
                placeholder="My Workspace"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleCreateWorkspace();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateDialogOpen(false);
                setNewWorkspaceName("");
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button onClick={handleCreateWorkspace} disabled={isCreating}>
              {isCreating ? "Creating..." : "Create Workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
